/**
 * CSV import constants — column schema, value caps, and the validation
 * tables used by the row validator. Centralized so adding a column means
 * touching one file (plus the validator) instead of a dozen call sites.
 */

import { Gender, Species } from '../../../../types/gql-op-types';

export const CSV_HEADERS = [
  // Required: pet identity + owner contact + birth year
  'microchip_id',
  'pet_name',
  'species',
  'gender',
  'year_of_birth',
  'owner_first_name',
  'owner_last_name',
  'owner_email',
  'owner_phone',
  // Optional pet attributes
  'breed',
  'month_of_birth',
  'day_of_birth',
  'weight',
  'color',
  // Optional owner address
  'owner_street',
  'owner_apt',
  'owner_city',
  'owner_state',
  'owner_zip',
] as const;

export type CsvHeader = (typeof CSV_HEADERS)[number];

/** Type guard for narrowing arbitrary header strings to known columns. */
export function isCsvHeader(value: string): value is CsvHeader {
  return (CSV_HEADERS as readonly string[]).includes(value);
}

export const REQUIRED_HEADERS: CsvHeader[] = [
  'microchip_id',
  'pet_name',
  'species',
  'gender',
  'year_of_birth',
  'owner_first_name',
  'owner_last_name',
  'owner_email',
  'owner_phone',
];

/** Cap on data rows (header excluded). Bounds payload size + UI scroll. */
export const MAX_ROWS = 500;

/**
 * Cap on raw file size in bytes BEFORE we read it into memory. 500 rows of
 * realistic data ≈ 40 KB; 1 MB is generous headroom for very wide rows and
 * stray BOMs without letting a multi-MB file OOM the browser tab.
 */
export const MAX_FILE_BYTES = 1 * 1024 * 1024;

/** Max characters per text field, keyed by snake_case CSV column name.
 *  Numeric columns (year/month/day/weight) and `microchip_id` are
 *  validated by their own range/format checks. `species` and `gender`
 *  are validated against enum membership, which always fails long before
 *  a length check could fire — so they're omitted here. */
export const MAX_LENGTHS: Partial<Record<CsvHeader, number>> = {
  pet_name: 100,
  breed: 64,
  color: 64,
  owner_first_name: 64,
  owner_last_name: 64,
  owner_email: 254, // RFC 5321
  owner_phone: 32,
  owner_street: 128,
  owner_apt: 32,
  owner_city: 64,
  owner_state: 64,
  owner_zip: 16,
};

export const MIN_BIRTH_YEAR = 1990;
/**
 * Max allowed birth year. Computed on each call so a long-lived browser
 * session (or a build older than the current calendar year) doesn't
 * reject pets born "this year" because the module load froze the value.
 */
export function getMaxBirthYear(): number {
  return new Date().getFullYear();
}
// User-facing cap is in pounds — the CSV column accepts lbs (everywhere
// else in the rescue portal does too) and the row mapper converts to kg
// before submit. 440 lbs ≈ 200 kg covers Great Danes, English Mastiffs,
// and anything else a rescue would realistically intake.
export const MAX_WEIGHT_LBS = 440;

// CSV formula-injection leaders: a cell starting with any of these is
// interpreted as a formula by Excel/Sheets when the data is later exported.
// We reject rather than silently strip — the rescue user likely meant a
// literal string, and quietly mutating their data would be surprising.
export const FORMULA_LEADERS = /^[=+\-@\t\r]/;

// Fields exempt from the FORMULA_LEADERS check. `owner_phone` commonly
// arrives with a `+1` country-code prefix from shelter-software exports;
// only the normalized 10-digit form is submitted, so a leading `+` or `-`
// can never reach an exported CSV as a formula.
export const FORMULA_LEADER_EXEMPT_FIELDS: ReadonlySet<CsvHeader> = new Set(['owner_phone']);

// Anything not printable that has no business in a pet/owner field. Tabs,
// newlines, NUL, etc. We allow standard whitespace ' ' (already trimmed at
// the cell level) but disallow every other control byte — including tab
// (\x09), LF (\x0A), and CR (\x0D) which can sneak in mid-cell via quoted
// CSV values.
// biome-ignore lint/suspicious/noControlCharactersInRegex: intentional — this
// is the set we're explicitly rejecting.
export const CONTROL_CHARS = /[\x00-\x1F\x7F]/;

// Enum acceptance — backend mutation expects these exact tokens. We accept
// any case in the CSV and uppercase before submit.
export const VALID_SPECIES: ReadonlySet<string> = new Set(Object.values(Species));
export const VALID_GENDERS: ReadonlySet<string> = new Set(Object.values(Gender));

// Days-per-month for calendar-correctness check. February uses 29 to allow
// leap years; we leap-year-check explicitly when day === 29.
export const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/**
 * Substitute for a blank `month_of_birth` cell. The GraphQL input accepts
 * a null month, but defaulting to January gives the imported pet a stable
 * month-of-birth value when rescues only know the year — useful for
 * birthday-driven UI like the pet profile card and reminder emails.
 */
export const DEFAULT_MONTH_OF_BIRTH = 1;

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

// ── Friendly labels for the "Get the template" step ───
// `colname` values must match `CSV_HEADERS` exactly so the labels stay in
// sync with what the parser accepts.

export const REQUIRED_FIELDS: { label: string; colname: CsvHeader }[] = [
  { label: 'Microchip number', colname: 'microchip_id' },
  { label: 'Pet name', colname: 'pet_name' },
  { label: 'Species (DOG / CAT / OTHER)', colname: 'species' },
  { label: 'Sex (MALE / FEMALE)', colname: 'gender' },
  { label: 'Birth year (YYYY)', colname: 'year_of_birth' },
  { label: 'Owner first name', colname: 'owner_first_name' },
  { label: 'Owner last name', colname: 'owner_last_name' },
  { label: 'Owner email', colname: 'owner_email' },
  { label: 'Owner phone (10-digit US)', colname: 'owner_phone' },
];

export const OPTIONAL_FIELDS: { label: string; colname: CsvHeader }[] = [
  // Pet attributes
  { label: 'Breed', colname: 'breed' },
  { label: 'Birth month (1-12, defaults to 1)', colname: 'month_of_birth' },
  { label: 'Birth day (1-31)', colname: 'day_of_birth' },
  { label: 'Weight (lbs)', colname: 'weight' },
  { label: 'Color', colname: 'color' },
  // Owner address
  { label: 'Owner street', colname: 'owner_street' },
  { label: 'Owner apt / unit', colname: 'owner_apt' },
  { label: 'Owner city', colname: 'owner_city' },
  { label: 'Owner state', colname: 'owner_state' },
  { label: 'Owner ZIP', colname: 'owner_zip' },
];

export const TEMPLATE_FILE_NAME = 'chip-pet-import-template.csv';

/** Human-friendly byte size: "37 KB", "1.5 MB", "248 B". */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Columns must match `CSV_HEADERS` order exactly — these are the rows users
// download to model their own data against, so a shifted value here cascades
// into every uploaded file. Weight values are in POUNDS (matches the rest of
// the rescue portal); the row mapper converts to kg before submit.
const TEMPLATE_SAMPLE_ROWS = [
  '981020012345678,Buddy,DOG,MALE,2021,Jane,Doe,jane@example.com,555-123-4567,Labrador Retriever,5,12,72,Black,123 Main St,Apt 4,Springfield,IL,62701',
  '981020087654321,Whiskers,CAT,FEMALE,2019,John,Smith,john@example.com,555-987-6543,Domestic Shorthair,,,10,Tabby,,,,,',
];

export const TEMPLATE_CSV = [CSV_HEADERS.join(','), ...TEMPLATE_SAMPLE_ROWS].join('\n');
